import { useState, useEffect, useMemo, useRef  } from 'react'
import Cookies from 'js-cookie'

import TopBar from '../../components/TopBar'
import { adminUpdate, adminGet } from '../../api/adminUser'

import userImage from '../../assets/images/user-icon.png'
import styles from './page.module.css'

const UserSetting = () => {

    const adminUserData = useMemo(() => JSON.parse(Cookies.get('adminUserData') || '{}'), [])
    const adminId = adminUserData?.adminUserId || null
    const [name, setName] = useState(adminUserData?.name || "")
    const [email, setEmail] = useState(adminUserData?.email || "")
    const [mobileNumber, setMobileNumber] = useState(adminUserData?.mobileNumber || "")
    const [password, setPassword] = useState("")
    const [selectedFeatureImage, setSelectedFeatureImage] = useState('');
    const [errors, setErrors] = useState({})
    const [loading, setLoading] = useState(false)
    const [message, setMessage] = useState("")

    const profileInputRef = useRef(null);

    const [profilePreview, setProfilePreview] = useState(
        selectedFeatureImage?.[0]?.url || ""
    );
    const [profileFile, setProfileFile] = useState(null);

    const handleProfileImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const validTypes = ["image/jpeg", "image/png"];
        if (!validTypes.includes(file.type)) {
            setErrors(prev => ({ ...prev, file: "Only PNG or JPG images allowed" }));
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            setProfilePreview(reader.result);
            setProfileFile(file);
            setErrors(prev => ({ ...prev, file: "" }));
        };

        reader.readAsDataURL(file);

        // reset input so same file can be re-selected
        e.target.value = "";
    };

    useEffect(() => {
        const fetchUserData = async () => {
            if (!adminId) return
            try {
                const response = await adminGet(adminId)

                setName(response.data.name || "")
                setEmail(response.data.email || "")
                setMobileNumber(response.data.mobileNumber || "")
                setSelectedFeatureImage([response?.data?.media] || "")
            } catch (error) {
                console.error('Error fetching user data:', error)
            }
        }
        fetchUserData()
    }, [adminId])

    const validateForm = () => {
        let newErrors = {};

        if (!name.trim()) {
            newErrors.name = "Name is required";
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email.trim()) {
            newErrors.email = "Email is required";
        } else if (!emailRegex.test(email)) {
            newErrors.email = "Enter a valid email address";
        }

        const phoneRegex = /^[0-9]{10}$/;
        if (!mobileNumber.trim()) {
            newErrors.mobileNumber = "Phone number is required";
        } else if (!phoneRegex.test(mobileNumber)) {
            newErrors.mobileNumber = "Enter valid 10-digit mobile number";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) return;
        if(!adminId) return

        setLoading(true)
        setMessage("")

        // const payload = {
        //     name,
        //     email,
        //     mobileNumber,
        //     ...(password && { password }),
        //     mediaId: selectedFeatureImage?.[0]?.id
        // }

        try {
             const formData = new FormData();

            formData.append("name", name);
            formData.append("email", email);
            formData.append("mobileNumber", mobileNumber);

            if (password) {
                formData.append("password", password);
            }

            // NEW FILE UPLOAD
            if (profileFile) {
                formData.append("mediaId", profileFile);
            }

            const response = await adminUpdate(adminId, formData);

            // Update cookies with new data
            const updatedData = { ...adminUserData, name, email, mobileNumber, mediaId: selectedFeatureImage?.[0]?.id }
            Cookies.set('adminUserData', JSON.stringify(updatedData))
            localStorage.setItem('userImage', response?.data?.media?.url)
            window.dispatchEvent(new CustomEvent('userImageUpdated'))
        } catch (error) {
            console.log({error})

            setMessage(error?.response?.data?.error?.message || "Failed to update profile")
        } finally {
            setLoading(false)
        }
    };

    return (
        <>
            <TopBar />
            <div className={styles.generalContent}>
                <div className={styles.generalRow}>
                    <div className={styles.formControltems}>
                        <div className={styles.formControlInputs}>
                            <div className={styles.formControlFields}>
                                <label className={styles.fieldsLabel}>Name</label>
                                <input
                                    type="text"
                                    value={name}
                                    className={styles.inputFields}
                                    onChange={(e) => {
                                        setName(e.target.value);
                                        setErrors(prev => ({ ...prev, name: "" }));
                                    }}

                                    autoComplete="off"
                                    placeholder='Enter name'
                                />
                                {errors.name && (
                                    <div className='error-message'>{errors.name}</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.generalRow}>
                    <div className={styles.formControltems}>
                        <div className={`${styles.formControlInputs} ${styles.formControlFull}`}>
                            <div className={styles.formControlFields}>
                                <label className={styles.fieldsLabel}>Email</label>

                                <div className={`${styles.inputFieldsIcon} ${styles.userEmail}`}>
                                    {/* <img src={iconEmail} alt="email-icon" /> */}
                                    <input
                                        type="text"
                                        className={styles.inputFields}
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            setErrors(prev => ({ ...prev, email: "" }));
                                        }}
                                        autoComplete="off"
                                        placeholder='Enter email'
                                    />
                                </div>

                                {errors.email && (
                                    <div className='error-message'>{errors.email}</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* PHONE ONLY — Country removed */}
                <div className={styles.generalRow}>
                    <div className={styles.formControltems}>
                        <div className={styles.formControlInputs}>
                            <div className={styles.formControlFields}>
                                <label className={styles.fieldsLabel}>Mobile number</label>
                                <div className={styles.inputFieldsIcon}>
                                    {/* <img src={iconPhone} alt="mobileNumber" /> */}
                                    <input
                                        className={styles.inputFields}
                                        type="number"
                                        value={mobileNumber}
                                        autoComplete="off"
                                        onChange={(e) => {
                                            setMobileNumber(e.target.value);
                                            setErrors(prev => ({ ...prev, mobileNumber: "" }));
                                        }}
                                        placeholder='Enter mobile number'
                                    />
                                </div>

                                {errors.mobileNumber && (
                                    <div className='error-message'>{errors.mobileNumber}</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.generalRow}>
                    <div className={styles.formControltems}>
                        <div className={`${styles.formControlInputs} ${styles.formControlFull}`}>
                            <div className={styles.formControlFields}>
                                <label className={styles.fieldsLabel}>Password</label>
                                <div className={`${styles.inputFieldsIcon} ${styles.userEmail}`}>
                                    <input
                                        type="password"
                                        className={styles.inputFields}
                                        value={password}
                                        onChange={(e) => {
                                            setPassword(e.target.value);
                                            setErrors(prev => ({ ...prev, password: "" }));
                                        }}
                                        autoComplete="new-password"
                                        placeholder='Enter new password (leave blank to keep current)'
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* PROFILE SECTION — same structure */}
                <div className={styles.generalRow}>
                    <label className={styles.fieldsLabel}>Profile picture</label>
                    <input
                        type="file"
                        accept="image/png, image/jpeg"
                        ref={profileInputRef}
                        hidden
                        onChange={handleProfileImageChange}
                    />
                    <div className={styles.profilePictureItems}>
                        <div className={styles.avatarBlock} onClick={() => profileInputRef.current?.click()}>
                            <div className={styles.avatarUpload}>
                                <img src={profilePreview || userImage} className={styles.profileImagePreview} alt="profile" />
                            </div>
                        </div>

                        <div
                            className={styles.newPageRightRow}
                            onClick={() => profileInputRef.current?.click()}
                        >
                            <div className={styles.fileUploadProfileBox}>
                                <p className={styles.profileImageText}>
                                    <span>Profile picture</span><br />
                                    <button className={styles.uploadBtn}>Replace</button>
                                </p>
                            </div>
                        </div>
                    </div>

                    {errors.file && <div className='error-message'>{errors.file}</div>}
                </div>

                {message && <div className={message.includes('success') ? 'success-message' : 'error-message'}>{message}</div>}

                <button className={`${styles.saveButton} ${styles.saveChangeButton}`} onClick={() => { handleSave() }} disabled={loading}>
                    {loading ? <div className='ringLoader'></div> : 'Save Changes'}
                </button>
            </div>
        </>
    )
}

export default UserSetting